import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Heading } from '@modusign/design-system';
import uniqueId from 'lodash/uniqueId';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useToggle } from 'react-use';

interface AccordionProps {
  index: number;
  title: string;
  summary: ReactNode | FC;
  children: ReactNode;
  isOpen?: boolean;
  required?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export default function Accordion({
  title,
  index,
  summary,
  children,
  isOpen,
  required,
  onToggle,
}: AccordionProps) {
  const [expanded, toggleExpanded] = useToggle(false);
  const panelId = useState(() => `accordion-${uniqueId()}`)[0];

  useEffect(() => {
    if (isOpen !== undefined) {
      toggleExpanded(isOpen);
    }
  }, [isOpen, toggleExpanded]);

  return (
    <Box as="section" borderBottom="1px solid" borderBottomColor="dividerNeutralLight" py={7}>
      <Flex
        alignItems="center"
        aria-controls={panelId}
        aria-expanded={expanded}
        as="button"
        height="46px"
        type="button"
        width="100%"
        onClick={() => {
          toggleExpanded();
          onToggle?.(!expanded);
        }}
      >
        <Flex alignItems="center" flex="none" width="200px">
          <Flex
            alignItems="center"
            bg={required ? 'statusBackgroundPrimaryBase' : 'statusBackgroundNeutralBase'}
            borderRadius="50%"
            fontSize="10px"
            fontWeight={required ? 'bold' : 'medium'}
            height="21px"
            justifyContent="center"
            width="21px"
          >
            {index}
          </Flex>
          <Heading as="h3" fontSize="md" ml={3}>
            {title}
          </Heading>
        </Flex>
        <Box flex="auto">
          {!expanded && (typeof summary === 'function' ? summary({}) : summary)}
        </Box>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
      </Flex>
      <Box display={expanded ? 'block' : 'none'} id={panelId} role="region">
        {children}
      </Box>
    </Box>
  );
}
